/**
 * KK-1215 · Dönemsel yükleme mutabakatı.
 *
 * Sistemdeki HAVALE yüklemeleri toplamı ile banka hesabına gelen toplam
 * karşılaştırılır. Mutabakatın anlamı iki BAĞIMSIZ kaynağın karşılaştırılması
 * olduğu için sistem toplamı istemciden GÖNDERİLMEZ; sunucu kendi hesaplar.
 * Bu ekran yalnızca banka ekstresindeki toplamı girer.
 *
 * Nakit yükleme banka hesabına gelmez; karşılaştırmaya girmez ama bağlam için
 * ayrı gösterilir — aksi hâlde "sistem toplamı neden düşük" sorusu her dönem
 * yeniden sorulur.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  Buton,
  Kart,
  Rozet,
  Tablo,
  TabloBaslik,
  TabloGovde,
  Tutar,
  Uyari,
} from "@kantin/ui";
import { IstatistikKarosu } from "@kantin/ui/grafik";
import { para } from "@kantin/shared";
import { apiOku, apiYaz } from "./api.js";
import {
  IslemDurumu,
  MetinAlani,
  Sayfa,
  SayfaBasligi,
  SorguDurumu,
  ayBasi,
  bugun,
  paraGecerliMi,
  paraNormalize,
  tarihGoster,
  tarihSaatGoster,
} from "./ortak.js";

interface MutabakatKaydi {
  id: string;
  donem_bas: string;
  donem_bit: string;
  sistem_toplami: string;
  banka_toplami: string;
  fark: string;
  mutabik_mi: boolean;
  aciklama: string | null;
  olusma_zamani: string;
  personel_ad: string | null;
}

interface MutabakatYaniti {
  donem_bas: string;
  donem_bit: string;
  sistem_havale_toplami: string;
  havale_adedi: number;
  nakit_toplami: string;
  nakit_adedi: number;
  gecmis_mutabakatlar: MutabakatKaydi[];
}

export function YuklemeMutabakati() {
  const istemci = useQueryClient();
  const [bas, setBas] = useState(ayBasi());
  const [bit, setBit] = useState(bugun());
  const [bankaToplami, setBankaToplami] = useState("");
  const [aciklama, setAciklama] = useState("");
  const [gonderildi, setGonderildi] = useState(false);

  const sorgu = useQuery({
    queryKey: ["yukleme-mutabakat", bas, bit],
    queryFn: () =>
      apiOku<MutabakatYaniti>("/yukleme/mutabakat", {
        donem_bas: bas,
        donem_bit: bit,
      }),
  });

  const kaydet = useMutation({
    mutationFn: () =>
      apiYaz<MutabakatKaydi>("POST", "/yukleme/mutabakat", {
        donem_bas: bas,
        donem_bit: bit,
        banka_toplami: paraNormalize(bankaToplami),
        ...(aciklama.trim() ? { aciklama: aciklama.trim() } : {}),
      }),
    onSuccess: () => {
      setBankaToplami("");
      setAciklama("");
      setGonderildi(false);
      void istemci.invalidateQueries({ queryKey: ["yukleme-mutabakat"] });
    },
  });

  const sistem = sorgu.data?.sistem_havale_toplami ?? "0.00";
  // Önizleme farkı: banka − sistem. Decimal aritmetiği @kantin/shared'dan gelir;
  // hiçbir aşamada number'a düşülmez (ADR-03).
  const onizlemeFark =
    paraGecerliMi(bankaToplami) && sorgu.data
      ? para(paraNormalize(bankaToplami)).minus(para(sistem)).toFixed(2)
      : null;

  const bankaHatasi =
    gonderildi && !paraGecerliMi(bankaToplami)
      ? "Banka toplamını 12345,67 biçiminde girin."
      : undefined;

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Yükleme mutabakatı"
        aciklama="Dönemsel olarak sistemdeki havale yüklemeleri toplamı ile banka hesabına gelen toplam karşılaştırılır."
      />

      <Kart baslik="Dönem">
        <div className="grid gap-3 md:grid-cols-3">
          <Alan
            etiket="Başlangıç"
            type="date"
            value={bas}
            onChange={(e) => setBas(e.target.value)}
          />
          <Alan
            etiket="Bitiş"
            type="date"
            value={bit}
            onChange={(e) => setBit(e.target.value)}
          />
        </div>
      </Kart>

      <SorguDurumu
        yukleniyor={sorgu.isPending}
        hata={sorgu.error}
        bos={!sorgu.data}
        bosBaslik="Dönem verisi yok"
        yenile={() => void sorgu.refetch()}
      >
        {sorgu.data && (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <IstatistikKarosu
                etiket="Sistem havale toplamı"
                deger={
                  <Tutar
                    deger={sorgu.data.sistem_havale_toplami}
                    yonsuz
                    boy="2xl"
                    etiket="sistem havale toplamı"
                  />
                }
                yardimci={`${sorgu.data.havale_adedi} işlem`}
              />
              <IstatistikKarosu
                etiket="Banka toplamı"
                deger={
                  onizlemeFark === null ? (
                    <span className="text-murekkep-3">—</span>
                  ) : (
                    <Tutar
                      deger={paraNormalize(bankaToplami)}
                      yonsuz
                      boy="2xl"
                      etiket="banka toplamı"
                    />
                  )
                }
                yardimci="Ekstreden elle girilir"
              />
              <IstatistikKarosu
                etiket="Fark (banka − sistem)"
                deger={
                  onizlemeFark === null ? (
                    <span className="text-murekkep-3">—</span>
                  ) : (
                    <Tutar
                      deger={onizlemeFark}
                      isaretiGoster
                      boy="2xl"
                      etiket="fark"
                    />
                  )
                }
                yardimci={
                  onizlemeFark === null
                    ? "Banka toplamını girin"
                    : onizlemeFark === "0.00"
                      ? "Mutabık"
                      : "Açıklanması gerekir"
                }
                vurgu={onizlemeFark !== null && onizlemeFark !== "0.00"}
              />
              <IstatistikKarosu
                etiket="Nakit yükleme (karşılaştırma dışı)"
                deger={
                  <Tutar
                    deger={sorgu.data.nakit_toplami}
                    yonsuz
                    boy="2xl"
                    etiket="nakit yükleme toplamı"
                  />
                }
                yardimci={`${sorgu.data.nakit_adedi} işlem · banka hesabına gelmez`}
              />
            </div>

            <Kart baslik="Mutabakat kaydı oluştur">
              <form
                className="flex flex-col gap-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  setGonderildi(true);
                  if (!paraGecerliMi(bankaToplami)) return;
                  kaydet.mutate();
                }}
              >
                <div className="grid gap-3 md:grid-cols-2">
                  <Alan
                    etiket="Banka ekstresindeki toplam"
                    value={bankaToplami}
                    onChange={(e) => setBankaToplami(e.target.value)}
                    inputMode="decimal"
                    ipucu="Yalnızca bu dönemde okulun IBAN'ına gelen öğrenci yüklemeleri."
                    {...(bankaHatasi ? { hata: bankaHatasi } : {})}
                  />
                </div>

                <MetinAlani
                  etiket="Açıklama"
                  value={aciklama}
                  onChange={(e) => setAciklama(e.target.value)}
                  rows={2}
                  ipucu="Fark varsa sebebini yazın: geç işlenen havale, banka masrafı, yanlış eşleştirme..."
                />

                {onizlemeFark !== null && onizlemeFark !== "0.00" && (
                  <Uyari tur="uyari" baslik="Fark var">
                    Pozitif fark: bankaya gelmiş ama sisteme işlenmemiş para
                    var. Negatif fark: sisteme işlenmiş ama bankada karşılığı
                    görünmeyen yükleme var. İkisi de{" "}
                    <strong>açıklanmadan kapatılmamalıdır</strong>.
                  </Uyari>
                )}

                <IslemDurumu hata={kaydet.error} />

                {kaydet.data && (
                  <Uyari
                    tur={kaydet.data.mutabik_mi ? "onay" : "uyari"}
                    baslik={
                      kaydet.data.mutabik_mi
                        ? "Dönem mutabık"
                        : "Mutabakat kaydı oluşturuldu — fark var"
                    }
                  >
                    Sistem:{" "}
                    <Tutar deger={kaydet.data.sistem_toplami} yonsuz boy="sm" />{" "}
                    · Banka:{" "}
                    <Tutar deger={kaydet.data.banka_toplami} yonsuz boy="sm" />{" "}
                    · Fark:{" "}
                    <Tutar deger={kaydet.data.fark} isaretiGoster boy="sm" />
                  </Uyari>
                )}

                <div>
                  <Buton
                    type="submit"
                    tur="birincil"
                    yukleniyor={kaydet.isPending}
                  >
                    Mutabakatı kaydet
                  </Buton>
                </div>
              </form>
            </Kart>

            <Kart baslik="Geçmiş mutabakatlar">
              <SorguDurumu
                yukleniyor={false}
                hata={null}
                bos={sorgu.data.gecmis_mutabakatlar.length === 0}
                bosBaslik="Kayıtlı mutabakat yok"
                bosAciklama="Bu dönemle kesişen bir mutabakat kaydı bulunmuyor."
              >
                <Tablo>
                  <TabloBaslik>
                    <th scope="col">Dönem</th>
                    <th scope="col">Sistem</th>
                    <th scope="col">Banka</th>
                    <th scope="col">Fark</th>
                    <th scope="col">Kaydeden</th>
                    <th scope="col">Açıklama</th>
                  </TabloBaslik>
                  <TabloGovde>
                    {sorgu.data.gecmis_mutabakatlar.map((m) => (
                      <tr key={m.id}>
                        <td className="whitespace-nowrap text-sm">
                          {tarihGoster(m.donem_bas)} –{" "}
                          {tarihGoster(m.donem_bit)}
                          <span className="block text-xs text-murekkep-3">
                            {tarihSaatGoster(m.olusma_zamani)}
                          </span>
                        </td>
                        <td className="text-right">
                          <Tutar deger={m.sistem_toplami} yonsuz boy="sm" />
                        </td>
                        <td className="text-right">
                          <Tutar deger={m.banka_toplami} yonsuz boy="sm" />
                        </td>
                        <td className="text-right">
                          <Tutar deger={m.fark} isaretiGoster boy="sm" />
                          <span className="ml-2">
                            <Rozet tur={m.mutabik_mi ? "onay" : "uyari"}>
                              {m.mutabik_mi ? "mutabık" : "fark var"}
                            </Rozet>
                          </span>
                        </td>
                        <td className="text-sm text-murekkep-2">
                          {m.personel_ad ?? "—"}
                        </td>
                        <td className="text-sm text-murekkep-2">
                          {m.aciklama ?? "—"}
                        </td>
                      </tr>
                    ))}
                  </TabloGovde>
                </Tablo>
              </SorguDurumu>
            </Kart>
          </>
        )}
      </SorguDurumu>
    </Sayfa>
  );
}
